import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
} from "antd";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PLANNING_USER_LIST } from "../../../../apiServices/API_ENDPOINTS";

import {
  PLANNING_REQUESTS,
  UN_PLANNING_BUYER_INQUIRY_LIST,
} from "../../../../apiServices/API_ENDPOINTS";
import {
  deleteData,
  getData,
  postData,
  putData,
} from "../../../../apiServices/common";

import { alertPop } from "apiServices/common/helper";
import CustomDatePicker from "common/CustomDatePicker";
import { isArrayAndHasValue } from "utils/functions";
import HandleNotification from "../../../../common/Notification";

const orderStatusOptions = [
  {
    id: 1,
    name: "Confirm",
    value: "Yes",
  },
  {
    id: 2,
    name: "Projection",
    value: "Projection",
  },
  {
    id: 3,
    name: "Waiting For Feedback",
    value: "Waiting For Feedback",
  },
];

const PlanningRequestForm = (props) => {
  const { form, editMode, viewMode } = props;
  const navigate = useNavigate();
  const { id } = useParams();
  const [userList, setUserList] = useState([]);
  const [inquiryList, setInquiryList] = useState([]);
  const [breakdowns, setBreakdowns] = useState({});
  const [inquiryShipmentDates, setInquiryShipmentDates] = useState({});
  const [mainRowOrderQty, setMainRowOrderQty] = useState({});
  const [planRequestDetails, setPlanRequestDetails] = useState({});
  const [currentPlanningData, setCurrentPlanningData] = useState(null);

  const queryParams = new URLSearchParams(window.location.search);
  const flag = queryParams.get("flag");
  const buyerInquiryId = queryParams.get("id");
  const orderStatusFromUrl = queryParams.get("order_status");

  const getUserList = useCallback(
    async (filterValues) => {
      const payload = {
        per_page: filterValues?.per_page || 10,
        ...filterValues,
      };
      const response = await getData(PLANNING_USER_LIST, false, payload);

      if (response && response?.data?.code === 200) {
        setUserList([response?.data?.data] || []);
        if (!id) {
          form.setFieldsValue({
            request_to: response?.data?.data?.id,
          });
        }
      } else {
        alertPop(
          "error",
          response?.messages ? response?.messages : "Something Went Wrong!",
        );
      }
    },
    [id, form],
  );

  const getInquiryList = useCallback(
    async (filterValues = {}) => {
      const payload = {
        ...filterValues,
        ...(buyerInquiryId ? { id: buyerInquiryId } : {}),
        ...(flag ? { flag: flag } : {}),
        order_status:
          filterValues?.order_status ||
          orderStatusFromUrl ||
          "Yes",
      };

      const response = await getData(
        UN_PLANNING_BUYER_INQUIRY_LIST,
        false,
        payload,
      );

      if (response && response?.status === 200) {
        setInquiryList(response?.data?.data?.data || []);
      } else {
        alertPop(
          "error",
          response?.messages ? response?.messages : "Something Went Wrong!",
        );
      }
    },
    [buyerInquiryId, flag, orderStatusFromUrl],
  );

  const getSelectedPlanningRequestInfo = useCallback(
    async (id) => {
      const query = `${PLANNING_REQUESTS}/${id}/show`;
      const response = await getData(query);

      if (response && response?.data?.code === 200) {
        const planningData = response?.data?.data;

        const plan_details = planningData?.plan_request_detail || [];
        const groupedDetails = {};

        const planRequestDetailsMap = {};
        plan_details.forEach((detail) => {
          planRequestDetailsMap[detail.inquiry_id] =
            planRequestDetailsMap[detail.inquiry_id] || [];
          planRequestDetailsMap[detail.inquiry_id].push(detail);
        });
        setPlanRequestDetails(planRequestDetailsMap);

        plan_details.forEach((detail) => {
          if (!groupedDetails[detail.inquiry_id]) {
            groupedDetails[detail.inquiry_id] = [];
          }
          groupedDetails[detail.inquiry_id].push({
            ...detail,
            detailId: detail.id,
          });
        });

        const inquiryIds = Object.keys(groupedDetails).map((id) =>
          parseInt(id),
        );

        const currentOrderStatus = planningData?.order_status || "Yes";

        form.setFieldsValue({
          request_date: planningData?.request_date
            ? moment(planningData.request_date)
            : moment(),
          request_to: planningData?.request_to?.id,
          request_by: planningData?.request_by?.name,
          order_status: currentOrderStatus,
        });

        setCurrentPlanningData(planningData);

        // Automatically fetch inquiries filtered by order_status as per planning request order_status
        const statusResponse = await getData(
          UN_PLANNING_BUYER_INQUIRY_LIST,
          false,
          { order_status: currentOrderStatus },
        );

        let statusInquiries = [];
        if (statusResponse && statusResponse?.status === 200) {
          statusInquiries = statusResponse?.data?.data?.data || [];
        }

        // Fetch any attached inquiries that might not be in statusInquiries
        const existingIds = new Set(statusInquiries.map((inq) => inq.id));
        const missingInquiryIds = inquiryIds.filter(
          (inqId) => !existingIds.has(inqId),
        );

        let missingInquiries = [];
        if (missingInquiryIds.length > 0) {
          const missingPromises = missingInquiryIds.map((inqId) =>
            getData(UN_PLANNING_BUYER_INQUIRY_LIST, false, {
              id: inqId,
              flag: "new",
            }),
          );
          const missingResponses = await Promise.all(missingPromises);
          missingResponses.forEach((res) => {
            if (res && res?.status === 200) {
              const inquiries = res?.data?.data?.data || [];
              missingInquiries = [...missingInquiries, ...inquiries];
            }
          });
        }

        const combinedInquiries = [...statusInquiries, ...missingInquiries];

        // Deduplicate by inquiry ID
        const uniqueInquiriesMap = new Map();
        combinedInquiries.forEach((inq) => {
          if (!uniqueInquiriesMap.has(inq.id)) {
            uniqueInquiriesMap.set(inq.id, inq);
          }
        });
        const allInquiries = Array.from(uniqueInquiriesMap.values());

        // Prepare breakdowns, main row quantities, shipment dates
        const initialBreakdowns = {};
        const initialMainRowQty = {};
        const initialShipmentDates = {};

        Object.entries(groupedDetails).forEach(([inquiryId, details]) => {
          if (details.length > 0) {
            const numId = parseInt(inquiryId);

            const [firstItem, ...restItems] = details;
            initialMainRowQty[numId] = firstItem.request_qty;

            initialBreakdowns[numId] = restItems.map((detail) => ({
              quantity: detail.request_qty,
              pcd: detail.pcd ? moment(detail.pcd) : null,
              shipment_date: detail.shipment_date
                ? moment(detail.shipment_date)
                : null,
              detailId: detail.id,
            }));

            if (firstItem.shipment_date) {
              initialShipmentDates[numId] = moment(firstItem.shipment_date);
            }
          }
        });

        setBreakdowns(initialBreakdowns);
        setMainRowOrderQty(initialMainRowQty);
        setInquiryShipmentDates((prev) => ({
          ...prev,
          ...initialShipmentDates,
        }));

        // Map inquiries: attached inquiries MUST be checked, with their PCD and details
        const updatedInquiries = allInquiries.map((inquiry) => {
          if (inquiryIds.includes(inquiry.id)) {
            const firstDetail = groupedDetails[inquiry.id]?.[0];
            return {
              ...inquiry,
              selected_pcd: firstDetail?.pcd
                ? moment(firstDetail.pcd)
                : inquiry.selected_pcd
                ? moment(inquiry.selected_pcd)
                : null,
              isChecked: true,
            };
          }
          return {
            ...inquiry,
            isChecked: false,
          };
        });

        // Place attached (checked) inquiries at the top
        const attached = updatedInquiries.filter((inq) =>
          inquiryIds.includes(inq.id),
        );
        const others = updatedInquiries.filter(
          (inq) => !inquiryIds.includes(inq.id),
        );

        setInquiryList([...attached, ...others]);
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Error in fetching planning request data",
          null,
        );
      }
    },
    [form],
  );

  const handleOrderStatusChange = (value) => {
    setBreakdowns({});
    setInquiryShipmentDates({});
    setMainRowOrderQty({});

    if (
      editMode &&
      id &&
      currentPlanningData &&
      value === (currentPlanningData?.order_status || "Yes")
    ) {
      getSelectedPlanningRequestInfo(id);
    } else {
      getInquiryList({ order_status: value });
    }
  };

  useEffect(() => {
    if (id) {
      getSelectedPlanningRequestInfo(id);
    } else {
      getInquiryList({ order_status: orderStatusFromUrl || "Yes" });
    }
  }, [id, editMode, getSelectedPlanningRequestInfo, getInquiryList, orderStatusFromUrl]);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const getTotalOrderQty = (inquiryId) => {
    const breakdownsTotal = getBreakdownTotal(inquiryId);
    const mainQty = parseInt(mainRowOrderQty[inquiryId] || 0);
    return breakdownsTotal + mainQty;
  };

  const getRemainingQty = (inquiry) => {
    const totalQty = getTotalOrderQty(inquiry.id);
    const remaining = inquiry.expected_order_quantity - totalQty;
    return remaining;
  };

  const isExceedingLimit = (inquiry) => {
    const remaining = getRemainingQty(inquiry);
    return remaining < 0;
  };

  const onFinishPlanningRequest = async (values) => {
    const checkedInquiries =
      isArrayAndHasValue(inquiryList) &&
      inquiryList?.filter((item) => item?.isChecked);

    if (
      isArrayAndHasValue(inquiryList) &&
      inquiryList.some((item) => item.isChecked && !item.selected_pcd)
    ) {
      HandleNotification(
        "error",
        "bottomRight",
        "PCD date is required for all inquiries. Please select PCD dates for all checked inquiries.",
        null,
      );
      return;
    }

    for (const inquiry of checkedInquiries) {
      const inquiryBreakdowns = breakdowns[inquiry.id] || [];
      if (inquiryBreakdowns.some((breakdown) => !breakdown.pcd)) {
        HandleNotification(
          "error",
          "bottomRight",
          `PCD date is required for all breakdown rows in inquiry ${inquiry.inquiry_no}.`,
          null,
        );
        return;
      }
    }

    for (const inquiry of checkedInquiries) {
      const inquiryBreakdowns = breakdowns[inquiry.id] || [];
      if (inquiryBreakdowns.length > 0) {
        const totalBreakdownQuantity = inquiryBreakdowns.reduce(
          (sum, breakdown) => sum + (parseInt(breakdown.quantity) || 0),
          0,
        );

        if (totalBreakdownQuantity > inquiry.expected_order_quantity) {
          HandleNotification(
            "error",
            "bottomRight",
            `Total breakdown quantity for inquiry ${inquiry.inquiry_no} exceeds the original order quantity.`,
            null,
          );
          return;
        }
      }
    }

    for (const inquiry of checkedInquiries) {
      if (isExceedingLimit(inquiry)) {
        HandleNotification(
          "error",
          "bottomRight",
          `Total order quantity for inquiry ${inquiry.inquiry_no} exceeds the original order quantity.`,
          null,
        );
        return;
      }
    }

    const buyer_inquiry = checkedInquiries.map((item) => {
      const inquiryBreakdowns = breakdowns[item.id] || [];

      const mainQty =
        mainRowOrderQty[item.id] !== undefined
          ? mainRowOrderQty[item.id]
          : item.expected_order_quantity;

      const details = [
        {
          request_qty: mainQty,
          pcd: item.selected_pcd
            ? moment(item.selected_pcd).format("YYYY-MM-DD")
            : null,
          shipment_date: inquiryShipmentDates[item.id]
            ? moment(inquiryShipmentDates[item.id]).format("YYYY-MM-DD")
            : item.expected_delivery_date
            ? moment(item.expected_delivery_date).format("YYYY-MM-DD")
            : null,
        },
      ];

      inquiryBreakdowns.forEach((breakdown) => {
        if (breakdown.quantity) {
          details.push({
            request_qty: breakdown.quantity,
            pcd: breakdown.pcd
              ? moment(breakdown.pcd).format("YYYY-MM-DD")
              : null,
            shipment_date: breakdown.shipment_date
              ? moment(breakdown.shipment_date).format("YYYY-MM-DD")
              : null,
          });
        }
      });

      return {
        inquiry_id: item.id,
        details: details,
      };
    });

    const query = editMode
      ? `${PLANNING_REQUESTS}/${id}`
      : `${PLANNING_REQUESTS}`;
    const bodyData = {
      request_date: moment(values?.request_date).format("YYYY-MM-DD"),
      request_to: values?.request_to,
      order_status: values?.order_status,
      buyer_inquiry: buyer_inquiry,
    };

    const response = editMode
      ? await putData(query, bodyData)
      : await postData(query, bodyData);

    if (response && response?.code === 200) {
      HandleNotification(
        "success",
        "bottomRight",
        `Planning Request ${editMode ? "updated" : "added"} successfully!`,
        null,
      );
      form.resetFields();
      navigate("/production-planning/planningRequests");
    } else {
      HandleNotification(
        "error",
        "bottomRight",
        `Error ${editMode ? "updating" : "adding"} Planning Request!`,
        null,
      );
    }
  };

  const onCheck = (id) => {
    const inquiryListCopy = isArrayAndHasValue(inquiryList)
      ? [...inquiryList]
      : [];

    const isExistsAndChecked = inquiryListCopy?.find(
      (item) => item?.id === id && item?.isChecked,
    );

    const foundIndex = inquiryListCopy?.findIndex((item) => item?.id === id);

    if (isExistsAndChecked) {
      inquiryListCopy[foundIndex]["isChecked"] = false;

      setBreakdowns((prev) => {
        const newBreakdowns = { ...prev };
        delete newBreakdowns[id];
        return newBreakdowns;
      });
    } else {
      inquiryListCopy[foundIndex]["isChecked"] = true;

      setBreakdowns((prev) => ({
        ...prev,
        [id]: [],
      }));
    }

    setInquiryList(inquiryListCopy);
  };

  const onPCDChange = (date, dateString, inquiryId) => {
    setInquiryList((prevList) =>
      prevList.map((item) =>
        item.id === inquiryId ? { ...item, selected_pcd: date } : item,
      ),
    );
  };

  const onShipmentDateChange = (date, dateString, inquiryId) => {
    setInquiryShipmentDates((prev) => ({
      ...prev,
      [inquiryId]: date,
    }));
  };

  const addBreakdownRow = (inquiryId) => {
    setBreakdowns((prev) => {
      const existingBreakdowns = prev[inquiryId] || [];

      const inquiry = inquiryList.find((item) => item.id === inquiryId);

      let shipmentDateToUse = null;

      if (existingBreakdowns.length > 0) {
        const lastBreakdown = existingBreakdowns[existingBreakdowns.length - 1];
        shipmentDateToUse = lastBreakdown.shipment_date;
      } else {
        shipmentDateToUse =
          inquiryShipmentDates[inquiryId] ||
          (inquiry?.expected_delivery_date
            ? moment(inquiry?.expected_delivery_date)
            : null);

        if (mainRowOrderQty[inquiryId] === undefined) {
          setMainRowOrderQty((prevState) => ({
            ...prevState,
            [inquiryId]: inquiry?.expected_order_quantity,
          }));
        }
      }

      return {
        ...prev,
        [inquiryId]: [
          ...existingBreakdowns,
          {
            quantity: "",
            pcd: inquiry?.selected_pcd || null,
            shipment_date: shipmentDateToUse,
          },
        ],
      };
    });
  };

  const updateMainRowQty = (inquiryId, value) => {
    setMainRowOrderQty((prev) => ({
      ...prev,
      [inquiryId]: value,
    }));
  };

  const updateBreakdownRow = (inquiryId, index, field, value) => {
    setBreakdowns((prev) => {
      const updatedBreakdowns = [...(prev[inquiryId] || [])];
      updatedBreakdowns[index] = {
        ...updatedBreakdowns[index],
        [field]: value,
      };
      return {
        ...prev,
        [inquiryId]: updatedBreakdowns,
      };
    });
  };

  const deleteBreakdownRow = (inquiryId, index) => {
    setBreakdowns((prev) => {
      const updatedBreakdowns = [...(prev[inquiryId] || [])];
      updatedBreakdowns.splice(index, 1);
      return {
        ...prev,
        [inquiryId]: updatedBreakdowns,
      };
    });
  };

  const getBreakdownTotal = (inquiryId) => {
    const inquiryBreakdowns = breakdowns[inquiryId] || [];
    return inquiryBreakdowns.reduce(
      (sum, breakdown) => sum + (parseInt(breakdown.quantity) || 0),
      0,
    );
  };

  const deletePlanningRequestDetail = async (detailId) => {
    try {
      const response = await deleteData(
        `/api/productionPlanning/planning_request_details_destroy/${detailId}`,
      );

      if (response && response.code === 200) {
        HandleNotification(
          "success",
          "bottomRight",
          "Planning request detail deleted successfully",
          null,
        );

        if (id) {
          getSelectedPlanningRequestInfo(id);
        }
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          response?.message || "Failed to delete planning request detail",
          null,
        );
      }
    } catch (error) {
      HandleNotification(
        "error",
        "bottomRight",
        "Error deleting planning request detail",
        null,
      );
    }
  };

  return (
    <div className="containt-body pt-3">
      <Form
        layout="vertical"
        form={form}
        name="control-hooks"
        onFinish={onFinishPlanningRequest}
      >
        <Row gutter={24}>
          <Col className="gutter-row" span={12}>
            <Form.Item
              label="Request Date"
              name="request_date"
              initialValue={moment()}
            >
              <CustomDatePicker
                disabled
                placeholder={"Request Date"}
                value={moment()}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Form.Item
              label="Request by"
              name="request_by"
              initialValue={localStorage.getItem("name") || "User"}
            >
              <Input placeholder="Entry Date" disabled size="small" />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Form.Item label="Request To" name="request_to">
              <Select
                style={{
                  width: "100%",
                }}
                placeholder="User List"
                optionFilterProp="children"
                aria-readonly="true"
                showSearch
                disabled
                onSearch={(value) => {
                  getUserList({ name: value });
                }}
              >
                {isArrayAndHasValue(userList) &&
                  userList.map((option) => {
                    return (
                      <option key={option.id} value={option.id}>
                        {option?.name}
                      </option>
                    );
                  })}
              </Select>
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Form.Item
              label="Order Status"
              name="order_status"
              initialValue={orderStatusFromUrl || "Yes"}
              rules={[{ required: true, message: "Please select order status" }]}
            >
              <Select
                placeholder="Select order status"
                optionFilterProp="children"
                showSearch
                disabled={viewMode}
                onChange={handleOrderStatusChange}
              >
                {orderStatusOptions.map((item) => (
                  <Select.Option value={item.value} key={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Card className="mt-3" title="Unplanned Inquiries and Styles">
        <div className="custome-table">
          <table border="1">
            <thead>
              <tr>
                <th key="checkbox_header" className="bc-java "></th>
                <th key="inquiry" className="bc-java ">
                  Inquiry
                </th>
                <th key="style_header" className="bc-java ">
                  Style
                </th>
                <th key="sub_style_header" className="bc-java ">
                  Sub Style
                </th>
                <th key="buyer_info" className="bc-java ">
                  Buyer
                </th>
                <th key="buying_office_info" className="bc-java ">
                  Buying Office
                </th>
                <th key="order_qty" className="bc-java ">
                  Order QTY
                </th>
                <th key="actual_order_qty" className="bc-java ">
                  Breakdown QTY
                </th>
                <th key="pcd" className="bc-java ">
                  PCD <span style={{ color: "red" }}>*</span>
                </th>
                <th key="shipment_date" className="bc-java ">
                  Shipment Date
                </th>
                <th key="actions" className="bc-java ">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isArrayAndHasValue(inquiryList) &&
                inquiryList?.map((inquiry, inquiryIndex) => {
                  let sub_style = Array.isArray(
                    inquiry?.style_inquiry_map?.style_info?.sub_style,
                  )
                    ? inquiry?.style_inquiry_map?.style_info?.sub_style
                    : [];
                  sub_style = sub_style
                    ?.map((item) => item?.sub_style_no)
                    ?.join(",");

                  const inquiryBreakdowns = breakdowns[inquiry.id] || [];
                  const totalBreakdownQty = getBreakdownTotal(inquiry.id);
                  const totalOrderQty = getTotalOrderQty(inquiry.id);
                  const remainingQty = getRemainingQty(inquiry);
                  const isOverLimit = isExceedingLimit(inquiry);

                  const rowSpan = inquiry?.isChecked
                    ? inquiryBreakdowns.length + 1
                    : 1;

                  return (
                    <>
                      <tr key={`carton_row_key_${inquiry?.id}`}>
                        <td
                          className="t-body sticky"
                          rowSpan={rowSpan}
                          style={{
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          <Checkbox
                            onChange={() => onCheck(inquiry?.id)}
                            checked={inquiry?.isChecked}
                            disabled={editMode && inquiry?.isChecked}
                          />
                        </td>
                        <td
                          className="t-body sticky"
                          rowSpan={rowSpan}
                          style={{
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {inquiry?.inquiry_no}
                        </td>
                        <td
                          className="t-body sticky"
                          rowSpan={rowSpan}
                          style={{
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {inquiry?.style_inquiry_map?.style_info?.style_no}
                        </td>
                        <td
                          className="t-body sticky"
                          rowSpan={rowSpan}
                          style={{
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {sub_style}
                        </td>

                        {/* Add the new buyer_info column */}
                        <td
                          className="t-body sticky"
                          rowSpan={rowSpan}
                          style={{
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {inquiry?.buyer_info?.name || "N/A"}
                        </td>

                        {/* New Buying Office column */}
                        <td
                          className="t-body sticky"
                          rowSpan={rowSpan}
                          style={{
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {inquiry?.buying_office_info?.name || "N/A"}
                        </td>

                        <td
                          className="t-body sticky"
                          rowSpan={rowSpan}
                          style={{
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {inquiry?.expected_order_quantity}
                          {inquiry?.isChecked && (
                            <div
                              style={{
                                fontSize: "10px",
                                color: isOverLimit ? "red" : "inherit",
                                fontWeight: "bold",
                                marginTop: "10px",
                              }}
                            >
                              Remaining QTY: {remainingQty}
                            </div>
                          )}
                        </td>
                        <td className="t-body sticky">
                          {inquiry?.isChecked && (
                            <InputNumber
                              placeholder="Qty"
                              value={
                                mainRowOrderQty[inquiry.id] !== undefined
                                  ? mainRowOrderQty[inquiry.id]
                                  : inquiry.expected_order_quantity
                              }
                              onChange={(value) =>
                                updateMainRowQty(inquiry.id, value)
                              }
                              style={{ width: "100%" }}
                              min={1}
                              max={inquiry.expected_order_quantity}
                              status={isOverLimit ? "error" : ""}
                            />
                          )}
                        </td>
                        <td className="t-body sticky">
                          <DatePicker
                            value={
                              inquiry.selected_pcd
                                ? moment(inquiry.selected_pcd)
                                : null
                            }
                            onChange={(date, dateString) =>
                              onPCDChange(date, dateString, inquiry.id)
                            }
                            disabled={!inquiry?.isChecked}
                            placeholder="Select PCD"
                            format="YYYY-MM-DD"
                            status={
                              inquiry?.isChecked && !inquiry?.selected_pcd
                                ? "error"
                                : ""
                            }
                            style={{ width: "100%" }}
                            required
                          />
                        </td>
                        <td className="t-body sticky">
                          <DatePicker
                            value={
                              inquiryShipmentDates[inquiry.id]
                                ? moment(inquiryShipmentDates[inquiry.id])
                                : inquiry?.expected_delivery_date
                                ? moment(inquiry?.expected_delivery_date)
                                : null
                            }
                            onChange={(date, dateString) =>
                              onShipmentDateChange(date, dateString, inquiry.id)
                            }
                            disabled={!inquiry?.isChecked}
                            placeholder="Shipment Date"
                            format="YYYY-MM-DD"
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td
                          className="t-body sticky"
                          style={{ textAlign: "center" }}
                        >
                          <Space>
                            {/* Show delete button in edit mode if there's a detail ID */}
                            {editMode &&
                              planRequestDetails[inquiry.id] &&
                              planRequestDetails[inquiry.id][0] && (
                                <Popconfirm
                                  title="Delete this item"
                                  description="Are you sure you want to delete this item?"
                                  onConfirm={() =>
                                    deletePlanningRequestDetail(
                                      planRequestDetails[inquiry.id][0].id,
                                    )
                                  }
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                  />
                                </Popconfirm>
                              )}
                            {/* Only show plus button if no breakdown rows or not in edit mode */}
                            {inquiry?.isChecked &&
                              inquiryBreakdowns.length === 0 && (
                                <Button
                                  type="link"
                                  icon={<PlusOutlined />}
                                  onClick={() => addBreakdownRow(inquiry.id)}
                                />
                              )}
                          </Space>
                        </td>
                      </tr>

                      {/* Breakdown rows */}
                      {inquiry?.isChecked &&
                        inquiryBreakdowns.map((breakdown, idx) => (
                          <tr
                            key={`breakdown_${inquiry.id}_${idx}`}
                            style={{ background: "#f9f9f9" }}
                          >
                            <td
                              className="t-body sticky"
                              style={{
                                verticalAlign: "middle",
                                textAlign: "center",
                              }}
                            >
                              <InputNumber
                                placeholder="Qty"
                                value={breakdown.quantity}
                                onChange={(value) =>
                                  updateBreakdownRow(
                                    inquiry.id,
                                    idx,
                                    "quantity",
                                    value,
                                  )
                                }
                                style={{ width: "100%" }}
                                min={1}
                                max={inquiry.expected_order_quantity}
                                status={isOverLimit ? "error" : ""}
                              />
                            </td>
                            <td
                              className="t-body sticky"
                              style={{ verticalAlign: "middle" }}
                            >
                              <DatePicker
                                value={
                                  breakdown.pcd ? moment(breakdown.pcd) : null
                                }
                                onChange={(date) =>
                                  updateBreakdownRow(
                                    inquiry.id,
                                    idx,
                                    "pcd",
                                    date,
                                  )
                                }
                                placeholder="Select PCD"
                                format="YYYY-MM-DD"
                                style={{ width: "100%" }}
                                status={!breakdown.pcd ? "error" : ""}
                                required
                              />
                            </td>
                            <td
                              className="t-body sticky"
                              style={{ verticalAlign: "middle" }}
                            >
                              <DatePicker
                                value={
                                  breakdown.shipment_date
                                    ? moment(breakdown.shipment_date)
                                    : null
                                }
                                onChange={(date) =>
                                  updateBreakdownRow(
                                    inquiry.id,
                                    idx,
                                    "shipment_date",
                                    date,
                                  )
                                }
                                placeholder="Shipment Date"
                                format="YYYY-MM-DD"
                                style={{ width: "100%" }}
                              />
                            </td>
                            <td
                              className="t-body sticky"
                              style={{
                                textAlign: "center",
                                verticalAlign: "middle",
                              }}
                            >
                              <Space
                                size="small"
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                {/* Show PopConfirm for delete in edit mode with detail ID */}
                                {editMode && breakdown.detailId ? (
                                  <Popconfirm
                                    title="Delete this item"
                                    description="Are you sure you want to delete this item?"
                                    onConfirm={() =>
                                      deletePlanningRequestDetail(
                                        breakdown.detailId,
                                      )
                                    }
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                    />
                                  </Popconfirm>
                                ) : (
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                      deleteBreakdownRow(inquiry.id, idx)
                                    }
                                  />
                                )}
                                {idx === inquiryBreakdowns.length - 1 && (
                                  <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    onClick={() => addBreakdownRow(inquiry.id)}
                                  />
                                )}
                              </Space>
                            </td>
                          </tr>
                        ))}
                    </>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default PlanningRequestForm;
